// externals
import type { Reducer } from 'redux';

// libraries
import type { OpenRepairOrder, OpenRepairOrdersState, VehicleRepairOrdersState } from '@makemydeal/dr-dash-types';

// consts/enums
import {
    OPEN_REPAIR_ORDERS_FETCH_FAILURE,
    OPEN_REPAIR_ORDERS_FETCH_REQUEST,
    OPEN_REPAIR_ORDERS_FETCH_SUCCESS
} from '../actionTypes/openRepairOrdersActionTypes';

export const initialState: OpenRepairOrdersState = { byVin: {} };

export type OpenRepairOrdersReducer = Reducer<OpenRepairOrdersState>;

// The requested vin, not the response's own, is the cache key: it is the one every lifecycle action
// carries, so a failed or mismatched response still resolves the entry it was issued for.
const getRequestedVin = (action: any): string | undefined => {
    const vin = action.meta?.vin;

    return typeof vin === 'string' && vin ? vin.toUpperCase() : undefined;
};

const setRepairOrdersForVin = (
    state: OpenRepairOrdersState,
    vin: string,
    vehicleState: VehicleRepairOrdersState
): OpenRepairOrdersState => ({
    ...state,
    byVin: { ...state.byVin, [vin]: vehicleState }
});

const removeRepairOrdersForVin = (state: OpenRepairOrdersState, vin: string): OpenRepairOrdersState => {
    if (!(vin in state.byVin)) {
        return state;
    }

    const byVin = { ...state.byVin };
    delete byVin[vin];

    return { ...state, byVin };
};

export const reducer: OpenRepairOrdersReducer = (state: OpenRepairOrdersState = initialState, action: any) => {
    const vin = getRequestedVin(action);

    switch (action.type) {
        case OPEN_REPAIR_ORDERS_FETCH_REQUEST: {
            if (!vin) {
                return state;
            }

            // Whatever is already held stays put while the car is re-read
            return setRepairOrdersForVin(state, vin, { repairOrders: state.byVin[vin]?.repairOrders ?? [], isLoading: true });
        }
        case OPEN_REPAIR_ORDERS_FETCH_SUCCESS: {
            if (!vin) {
                return state;
            }

            const response = action.payload;
            const isForRequestedVehicle = typeof response?.vin === 'string' && response.vin.toUpperCase() === vin;

            if (!isForRequestedVehicle || !Array.isArray(response.repairOrders)) {
                return removeRepairOrdersForVin(state, vin);
            }

            return setRepairOrdersForVin(state, vin, {
                repairOrders: response.repairOrders.filter((order: OpenRepairOrder) => typeof order?.unifiedServiceId === 'string')
            });
        }
        case OPEN_REPAIR_ORDERS_FETCH_FAILURE: {
            if (!vin) {
                return state;
            }

            // Dropped rather than emptied: a requested vin is never requested again, and a failure is
            // not an answer, so the next trigger for this car should be free to retry.
            return removeRepairOrdersForVin(state, vin);
        }
        default: {
            return state;
        }
    }
};
